// @ts-nocheck
/**
 * sst-bundle-picker.js — Combined Listings bundle picker
 *
 * Each color is a separate Shopify product linked via Combined Listings.
 * Pack tiers are defined in theme settings and rendered in Liquid.
 *
 * On color change: fetches /products/{handle}.js for that color's variants
 * and repopulates the size dropdown for that row.
 *
 * Cart: POSTs one line per unique selected variant, with quantity grouped.
 * A backend Shopify discount code handles bundle savings.
 */
(function () {
  'use strict';

  if (window._sstBundlePickerLoaded) return;
  window._sstBundlePickerLoaded = true;

  // ── Module-level state ────────────────────────────────────────────────────
  let SECTION_ID = '';
  let activeVariantPicker = null;
  let bundleActive = false;
  let currentPackLabel = null;
  let stickyOnePackSelection = { color: '', size: '' };
  let userSelectedPack = null;

  // colorProductMap: { colorName (lowercase) → { handle, url, sizes: [] } }
  // Populated from Combined Listings data on init and after each color fetch.
  const colorProductMap = new Map();

  // ── Helpers ───────────────────────────────────────────────────────────────

  function getProductFormComponent() {
    return document.querySelector(`product-form-component[data-section-id="${SECTION_ID}"]`);
  }

  function isOurProductForm(form) {
    return !!(
      form.closest(`product-form-component[data-section-id="${SECTION_ID}"]`) ||
      form.closest(`[data-section-id="${SECTION_ID}"]`)
    );
  }

  function getSelectedPackInput() {
    if (!activeVariantPicker) return null;

    if (userSelectedPack) {
      const radio = activeVariantPicker.querySelector(
        `.variant-option--pack input[type="radio"][value="${CSS.escape(userSelectedPack)}"]`
      );
      if (radio) return radio;
    }

    return (
      activeVariantPicker.querySelector('.variant-option--pack input[type="radio"]:checked') ||
      activeVariantPicker.querySelector('.variant-option--pack input[type="radio"][data-current-checked="true"]')
    );
  }

  function getSelectedPackLabel() {
    return getSelectedPackInput()?.value || null;
  }

  function getSelectedPackCount() {
    const input = getSelectedPackInput();
    const count = parseInt(input?.dataset.packCount, 10);
    return isNaN(count) ? 1 : count;
  }

  function getActiveSlot() {
    const packLabel = getSelectedPackLabel();
    if (!packLabel || !activeVariantPicker) return null;
    const radio = activeVariantPicker.querySelector(
      `.variant-option--pack input[type="radio"][value="${CSS.escape(packLabel)}"]`
    );
    return radio?.closest('.sst-pack-option')?.querySelector('.sst-bundle-rows-slot') || null;
  }

  function getErrorEl() {
    return getActiveSlot()?.querySelector('.sst-bundle-error') || null;
  }

  function showError(msg) {
    const el = getErrorEl();
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(() => { el.hidden = true; }, 6000);
  }

  function clearError() {
    const el = getErrorEl();
    if (el) el.hidden = true;
  }

  function setButtonsDisabled(disabled) {
    const pfc = getProductFormComponent();
    if (!pfc) return;
    pfc.querySelectorAll('[name="add"], add-to-cart-component button[type="submit"]')
       .forEach((btn) => { btn.disabled = disabled; });
  }

  function handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  // ── Build color→product map from Combined Listings data ───────────────────
  // The hidden Color fieldset inputs carry data-connected-product-url per value.

  function buildColorProductMapFromDOM(vp) {
    colorProductMap.clear();

    const colorFieldset = vp.querySelector('fieldset[data-option-name="Color"], fieldset[data-option-name="Colour"]');
    if (!colorFieldset) return;

    colorFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      const colorName = input.value;
      const productUrl = input.dataset.connectedProductUrl;
      if (!colorName) return;

      const key = colorName.toLowerCase();
      const existing = colorProductMap.get(key);
      if (!existing) {
        const handle = productUrl ? productUrl.split('/products/')[1]?.split('?')[0] : handleize(colorName);
        colorProductMap.set(key, { name: colorName, handle, url: productUrl || '', sizes: [] });
      }
    });
  }

  // ── Fetch sizes for a color via /products/{handle}.js ────────────────────

  async function fetchSizesForColor(colorName) {
    const entry = colorProductMap.get(colorName.toLowerCase());
    if (!entry) return [];
    if (entry.sizes.length > 0) return entry.sizes;

    const handle = entry.handle;
    if (!handle) return [];

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return [];
      const data = await res.json();

      const sizeIdx = data.options?.findIndex((o) => /size/i.test(typeof o === 'string' ? o : o.name)) ?? -1;

      if (sizeIdx === -1) return [];

      // Only include sizes that have at least one available variant
      const available = new Set();
      const allSizes = [];
      data.variants?.forEach((v) => {
        const size = v.options[sizeIdx];
        if (!size) return;
        if (!allSizes.includes(size)) allSizes.push(size);
        if (v.available) available.add(size);
      });

      entry.sizes = allSizes;
      entry.variantsBySizeKey = {};
      data.variants?.forEach((v) => {
        const size = v.options[sizeIdx];
        if (size) entry.variantsBySizeKey[size] = v;
      });
      entry.availableSizes = available;
      const fi = data.featured_image;
      entry.featuredImage = fi ? (typeof fi === 'string' ? fi : fi.src) : null;

      return allSizes;
    } catch (err) {
      console.error('[sst-bundle] fetch/parse error for', handle, err);
      return [];
    }
  }

  // ── Get product data for color (from cache) ───────────────────────────────

  function getColorEntry(colorName) {
    return colorProductMap.get(colorName?.toLowerCase()) || null;
  }

  // ── Row HTML builder ──────────────────────────────────────────────────────

  function buildBundleRowsHTML(n) {
    const colors = [...colorProductMap.values()].map((e) => e.name);
    const colorOptions = colors.map((c) => `<option value="${c}">${c}</option>`).join('');
    const caretSVG = `<svg aria-hidden="true" focusable="false" viewBox="0 0 10 6" width="10" height="6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>`;

    let html = `<div class="sst-bundle-rows">`;
    for (let i = 1; i <= n; i++) {
      html += `
        <div class="sst-bundle-row" data-row="${i}">
          <span class="sst-bundle-row__num">#${i}</span>
          <div class="sst-bundle-row__selects">
            <div class="sst-bundle-select-wrap sst-bundle-select-wrap--color">
              <img class="sst-bundle-select-thumb" alt="" hidden>
              <select class="sst-bundle-select sst-bundle-select--color" data-row="${i}" aria-label="Item ${i} color">
                ${colorOptions}
              </select>
              ${caretSVG}
            </div>
            <div class="sst-bundle-select-wrap">
              <select class="sst-bundle-select sst-bundle-select--size" data-row="${i}" aria-label="Item ${i} size">
                <option value="">Loading...</option>
              </select>
              ${caretSVG}
            </div>
          </div>
        </div>`;
    }
    html += `<div class="sst-bundle-error" hidden></div>`;
    html += `</div>`;
    return html;
  }

  function updateColorThumb(row, colorName) {
    const thumb = row.querySelector('.sst-bundle-select-thumb');
    if (!thumb) return;
    const entry = getColorEntry(colorName);
    const src = entry?.featuredImage;
    if (src) {
      thumb.src = src;
      thumb.alt = colorName;
      thumb.hidden = false;
    } else {
      thumb.hidden = true;
    }
  }

  async function populateSizeDropdown(row, colorName, restoreSize = '') {
    const sizeSelect = row.querySelector('.sst-bundle-select--size');
    if (!sizeSelect) return;

    sizeSelect.disabled = true;
    const sizes = await fetchSizesForColor(colorName);
    const entry = getColorEntry(colorName);

    sizeSelect.innerHTML = sizes.map((s) => {
      const unavail = entry?.availableSizes && !entry.availableSizes.has(s) ? ' (sold out)' : '';
      return `<option value="${s}"${unavail ? ' disabled' : ''}>${s}${unavail}</option>`;
    }).join('') || '<option value="">N/A</option>';

    if (restoreSize && sizeSelect.querySelector(`option[value="${CSS.escape(restoreSize)}"]`)) {
      sizeSelect.value = restoreSize;
    }
    sizeSelect.disabled = false;
  }

  // ── Row setup after pack switch ───────────────────────────────────────────

  async function updateRows() {
    if (!activeVariantPicker) return;

    activeVariantPicker.querySelectorAll('.sst-bundle-rows-slot').forEach((s) => {
      s.innerHTML = '';
      s.hidden = true;
    });

    currentPackLabel = getSelectedPackLabel();
    const currentPackCount = getSelectedPackCount();
    const slot = getActiveSlot();
    if (!slot) return;

    bundleActive = true;
    slot.innerHTML = buildBundleRowsHTML(currentPackCount);
    slot.hidden = false;

    // Populate size dropdowns for all rows (row 1 restores sticky selection)
    const rows = Array.from(slot.querySelectorAll('.sst-bundle-row'));
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const colorSelect = row.querySelector('.sst-bundle-select--color');
      if (!colorSelect) continue;

      const restoreColor = i === 0 ? stickyOnePackSelection.color : '';
      const restoreSize  = i === 0 ? stickyOnePackSelection.size  : '';

      if (restoreColor && colorSelect.querySelector(`option[value="${CSS.escape(restoreColor)}"]`)) {
        colorSelect.value = restoreColor;
      }

      await populateSizeDropdown(row, colorSelect.value, restoreSize);
      updateColorThumb(row, colorSelect.value);
    }

    const firstRow = slot.querySelector('.sst-bundle-row[data-row="1"]');
    const firstColor = firstRow?.querySelector('.sst-bundle-select--color')?.value;
    const firstSize = firstRow?.querySelector('.sst-bundle-select--size')?.value || '';
    if (firstColor) notifyFirstRowSelection(firstColor, firstSize);
  }

  // ── Sync sticky selection from first row ──────────────────────────────────

  function syncStickyFromFirstRow() {
    const slot = getActiveSlot();
    const firstRow = slot?.querySelector('.sst-bundle-row[data-row="1"]');
    if (!firstRow) return;
    const color = firstRow.querySelector('.sst-bundle-select--color')?.value || '';
    const size  = firstRow.querySelector('.sst-bundle-select--size')?.value  || '';
    if (color || size) stickyOnePackSelection = { color, size };
  }

  // ── Gallery notification ──────────────────────────────────────────────────

  function notifyFirstRowSelection(colorName, size = '') {
    const entry = getColorEntry(colorName);
    const variantImage = size ? entry?.variantsBySizeKey?.[size]?.featured_image : null;
    const imageSrc =
      (typeof variantImage === 'string' ? variantImage : variantImage?.src) ||
      entry?.featuredImage ||
      '';

    document.dispatchEvent(new CustomEvent('sst:bundle:first-selection-change', {
      bubbles: true,
      detail: {
        color: colorName,
        size,
        productUrl: entry?.url || '',
        imageSrc,
        productId: getProductFormComponent()?.dataset.productId || '',
        pack: currentPackLabel,
      },
    }));
  }

  // ── Intercept pack radio — capture phase, block Horizon ──────────────────

  document.addEventListener('change', (e) => {
    if (e.target.closest('.sst-bundle-rows-slot')) return;
    const packInput = e.target.closest(
      'variant-picker[data-sst-variants] .variant-option--pack input[type="radio"]'
    );
    if (!packInput) return;

    userSelectedPack = packInput.value;
    e.stopImmediatePropagation();

    const vp = packInput.closest('variant-picker[data-sst-variants]');
    if (vp) {
      vp.querySelectorAll('.sst-pack-option').forEach((label) => {
        label.classList.toggle('sst-pack-option--selected', label.contains(packInput));
      });
      vp.querySelectorAll('.variant-option--pack input[type="radio"]').forEach((r) => {
        r.dataset.currentChecked = (r === packInput) ? 'true' : 'false';
        r.dataset.previousChecked = 'false';
      });
    }

    updateRows();
  }, true);

  // ── React to row color/size changes ──────────────────────────────────────

  document.addEventListener('change', async (e) => {
    if (!e.target.closest('variant-picker[data-sst-variants] .sst-bundle-rows-slot')) return;

    const row = e.target.closest('.sst-bundle-row');
    if (!row) return;

    const isFirstRow = row.dataset.row === '1';
    const isColorSelect = e.target.matches('.sst-bundle-select--color');

    if (isColorSelect) {
      const newColor = e.target.value;
      const currentSize = row.querySelector('.sst-bundle-select--size')?.value || '';
      await populateSizeDropdown(row, newColor, currentSize);
      updateColorThumb(row, newColor);
    }

    if (isFirstRow) {
      syncStickyFromFirstRow();
      notifyFirstRowSelection(
        row.querySelector('.sst-bundle-select--color')?.value || '',
        row.querySelector('.sst-bundle-select--size')?.value || ''
      );
    }
  });

  // ── Document-level submit interceptor ────────────────────────────────────

  document.addEventListener('submit', (event) => {
    if (!bundleActive) return;
    if (!isOurProductForm(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    submitBundle();
  }, true);

  // Sticky add-to-cart uses a button outside the product form. Route its
  // bundle-specific event through the same cart submission path as the form.
  document.addEventListener('sst:bundle:add-first-pack', async (event) => {
    if (!activeVariantPicker) return;

    const eventProductId = event.detail?.productId?.toString();
    const pickerProductId = activeVariantPicker.dataset.productId?.toString();
    if (eventProductId && pickerProductId && eventProductId !== pickerProductId) return;

    if (!bundleActive || !getActiveSlot()?.querySelector('.sst-bundle-row')) {
      await updateRows();
    }

    submitBundle();
  });

  // ── Cart submission — group matching row selections by variant ────────────

  async function submitBundle() {
    clearError();

    const slot = getActiveSlot();
    const rows = Array.from(slot?.querySelectorAll('.sst-bundle-row') || []);

    if (rows.length === 0) {
      showError('Please select a pack option first.');
      return;
    }

    const lineItemsByVariant = new Map();

    for (let i = 0; i < rows.length; i++) {
      const row    = rows[i];
      const color  = row.querySelector('.sst-bundle-select--color')?.value || '';
      const size   = row.querySelector('.sst-bundle-select--size')?.value  || '';
      const rowNum = i + 1;

      if (!color) { showError(`Please select a color for item #${rowNum}.`); return; }
      if (!size)  { showError(`Please select a size for item #${rowNum}.`);  return; }

      const entry = getColorEntry(color);
      if (!entry) { showError(`Unknown color "${color}" for item #${rowNum}.`); return; }

      const variantData = entry.variantsBySizeKey?.[size];
      if (!variantData) {
        showError(`"${color} / ${size}" is not available for item #${rowNum}.`);
        return;
      }
      if (!variantData.available) {
        showError(`"${color} / ${size}" is sold out for item #${rowNum}. Please choose another.`);
        return;
      }

      const variantKey = String(variantData.id);
      const existingLine = lineItemsByVariant.get(variantKey);

      if (existingLine) {
        existingLine.quantity += 1;
      } else {
        lineItemsByVariant.set(variantKey, {
          id: variantData.id,
          quantity: 1,
          properties: {
            _bundle_pack: 'Bundle',
          },
        });
      }
    }

    const lineItems = Array.from(lineItemsByVariant.values());
    const totalQuantity = lineItems.reduce((sum, item) => sum + item.quantity, 0);

    setButtonsDisabled(true);

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lineItems,
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        showError(data.message || 'Could not add items to cart.');
        return;
      }

      const cartUpdateEvent = new Event('cart:update', { bubbles: true });
      cartUpdateEvent.detail = {
        resource: data,
        sourceId: getProductFormComponent()?.id || SECTION_ID,
        data: {
          source:    'product-form-component',
          itemCount: totalQuantity,
          productId: getProductFormComponent()?.dataset.productId,
          sections:  data.sections,
        },
      };
      document.dispatchEvent(cartUpdateEvent);
    } catch (err) {
      console.error('[sst-bundle-picker]', err);
      showError('Something went wrong. Please try again.');
    } finally {
      setButtonsDisabled(false);
    }
  }

  // ── Per-picker init ───────────────────────────────────────────────────────

  function initBundlePicker(vp) {
    const newSectionId = vp.dataset.sectionId || '';
    const isNewSection = newSectionId !== SECTION_ID;

    if (isNewSection) {
      userSelectedPack = null;
      stickyOnePackSelection = { color: '', size: '' };
      bundleActive = false;
    }

    SECTION_ID = newSectionId;
    activeVariantPicker = vp;
    vp._sstBundleInit = true;

    if (!vp.querySelector('.variant-option--pack')) return;

    buildColorProductMapFromDOM(vp);

    // Seed sticky from URL variant or selected variant JSON
    if (!stickyOnePackSelection.color) {
      const selectedJson = vp.querySelector('script[type="application/json"]')?.textContent;
      if (selectedJson) {
        try {
          const sv = JSON.parse(selectedJson);
          // With Combined Listings, variant options only have Size (and maybe Color).
          // Find the selected color from the hidden color fieldset.
          const colorInput = vp.querySelector(
            'fieldset[data-option-name="Color"] input[data-current-checked="true"], ' +
            'fieldset[data-option-name="Colour"] input[data-current-checked="true"]'
          );
          const sizeInput = vp.querySelector(
            'fieldset[data-option-name="Size"] input[data-current-checked="true"]'
          );
          const color = colorInput?.value || '';
          const size  = sizeInput?.value  || sv?.options?.[0] || '';
          if (color || size) stickyOnePackSelection = { color, size };
        } catch {}
      }
    }

    updateRows();

    // Retry in case Horizon resets checked state after our first read
    [100, 300].forEach((delay) => {
      setTimeout(() => {
        if (activeVariantPicker !== vp) return;
        if (!getActiveSlot()?.querySelector('.sst-bundle-row')) updateRows();
      }, delay);
    });
  }

  // ── Init all bundle pickers ───────────────────────────────────────────────

  function init() {
    document.querySelectorAll('variant-picker[data-sst-variants]').forEach((vp) => {
      if (vp._sstBundleInit) return;
      initBundlePicker(vp);
    });
  }

  // ── Watch for Horizon section re-renders ─────────────────────────────────

  let _initTimer = null;
  new MutationObserver(() => {
    clearTimeout(_initTimer);
    _initTimer = setTimeout(init, 0);
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
